import { inject, Injectable } from '@angular/core';
import { SupabaseClientService } from '../supabase-client/supabase-client.service';
import { AuthService } from '../supabase-auth/auth.service';
import { firstValueFrom, take } from 'rxjs';
import { FileCategory } from './storage-folder.enum';
@Injectable({
  providedIn: 'root',
})
export class SupabaseStorageService {
  private bucketName = 'app-assets';

  private supabaseClientService = inject(SupabaseClientService);
  private authService = inject(AuthService);

  private async getCurrentTenantCode(): Promise<string> {
    const tenantId = '';
    if (!tenantId) {
      throw new Error('User not logged in or no tenant assigned');
    }
    const { data: tenant, error } = await this.supabaseClientService.client
      .from('tenant')
      .select('tenantCode')
      .eq('id', tenantId)
      .single();

    if (error || !tenant?.tenantCode) {
      throw new Error('Tenant not found');
    }
    return tenant.tenantCode;
  }

  private async generateFilePath(
    file: File,
    category: FileCategory,
  ): Promise<string> {
    const tenantCode = await this.getCurrentTenantCode();
    // Structure: {tenantCode}/{category}/{timestamp}-{filename}
    return `${tenantCode}/${category}/${Date.now()}-${file.name}`;
  }

  async uploadFile(
    file: File,
    category: FileCategory,
  ): Promise<{
    id?: string;
    path?: string;
    fullPath?: string;
    error?: string;
  }> {
    try {
      if (!file) return { error: 'No file provided' };
      const { data: userData } =
        await this.supabaseClientService.client.auth.getUser();
      if (!userData.user) return { error: 'You must be signed in to upload.' };
      const filePath = await this.generateFilePath(file, category);
      const { error, data } = await this.supabaseClientService.client.storage
        .from(this.bucketName)
        .upload(filePath, file);
      if (error) return { error: error.message };

      return data;
    } catch (error: any) {
      return { error: error.message };
    }
  }

  async getPrivateImageUrl(
    bucketName: string,
    filePath: string,
    expiresIn = 3600,
  ): Promise<string> {
    const { data, error } = await this.supabaseClientService.client.storage
      .from(bucketName)
      .createSignedUrl(filePath, expiresIn);

    if (error) {
      throw error;
    }
    return data.signedUrl;
  }

  // Delete file
  async deleteFile(filePath: string): Promise<{ error?: string }> {
    const { error } = await this.supabaseClientService.client.storage
      .from(this.bucketName)
      .remove([filePath]);

    if (error) return { error: error.message };
    return {};
  }
}
